<template>
  <div>
    <Breadcrumbs
      :paths="['/translation_preset', $route.path]"
    ></Breadcrumbs>
    <v-toolbar>
      <v-toolbar-title>
        {{ $t("message.translation_preset.translation_preset_detail") }}&nbsp;({{ $route.params.tid }})
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <v-tooltip top>
        <template v-slot:activator="{ on }">
          <v-icon v-on="on" class="ma-2" @click="refresh()">
            mdi-refresh
          </v-icon>
        </template>
        <span>{{ $t("message.common.refresh") }}</span>
      </v-tooltip>
    </v-toolbar>
    <v-card class="pa-5 mt-1" v-if="fetch_status === 'getting'">
      {{ $t("message.common.getting", { "id": $route.params.tid }) }}
      <p class="text-right">
        <v-btn :to="get_prev(get_prefix() + 'module')">
          {{ $t("message.common.prev") }}
        </v-btn>
      </p>
    </v-card>
    <v-card class="pa-5 mt-1" v-else-if="data">
      <v-simple-table>
        <template v-slot:default>
          <tbody>
            <tr>
              <td>
                {{ $i18n.t("message.common.tid") }}
              </td>
              <td>
                {{ data.tid }}
              </td>
            </tr>
            <tr>
              <td>
                {{ $i18n.t("message.common.title") }}
              </td>
              <td class="table-name">
                <template v-if="changeable()">
                  <v-card
                    class="d-flex justify-space-between"
                    flat
                  >
                    <v-card flat class="align-self-center flex-grow-1">
                      <v-text-field
                        v-model="data.title"
                        :rules="[
                          v => (v && v.length <= 80) || $t('message.common.over_text', { length: 80 })
                        ]"
                        counter="80"
                      >
                      </v-text-field>
                    </v-card>
                    <template v-if="data.title !== original_data.title">
                      <v-tooltip top>
                        <template v-slot:activator="{ on }">
                          <v-icon v-on="on" class="ma-2" @click="data.title = original_data.title">
                            mdi-undo-variant
                          </v-icon>
                        </template>
                        <span>{{ $t("message.common.undo") }}</span>
                      </v-tooltip>
                    </template>
                  </v-card>
                </template>
                <template v-else>
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <span v-on="on">
                        {{ data.title }}
                      </span>
                    </template>
                    <span>{{ data.title }}</span>
                  </v-tooltip>
                </template>
              </td>
            </tr>
            <tr>
              <td>
                {{ $i18n.t("message.common.direction") }}
              </td>
              <td>
                {{ $t("message.common.lang_" + data.source_lang) }}
                &rarr;
                {{ $t("message.common.lang_" + data.target_lang) }}
              </td>
            </tr>
            <tr>
              <td>
                {{ $t("message.common.group") }}
              </td>
              <td>
                <IdName
                  :mode="mode"
                  type="group"
                  :ids="[data.gid]"
                  :nav_path="nav_path"
                ></IdName>
              </td>
            </tr>
            <tr>
              <td>
                {{ $t("message.common.user") }}
              </td>
              <td>
                <IdName
                  :mode="mode"
                  type="user"
                  :ids="[data.uid]"
                  :nav_path="nav_path"
                ></IdName>
              </td>
            </tr>
            <tr>
              <td>
                {{ $i18n.t("message.common.permission") }}
              </td>
              <td class="pt-0 pb-0">
                {{ $t('message.common.' + data.permission) }}
              </td>
            </tr>
            <tr>
              <td>
                {{ $i18n.t("message.common.ctime") }}
              </td>
              <td>
                {{ $d(1000 * data.ctime, 'long', $store.getters.langs[$i18n.locale].locale) }}
              </td>
            </tr>
            <tr>
              <td>
                {{ $i18n.t("message.common.etime") }}
              </td>
              <td>
                <template v-if="data.etime !== -1">
                  {{ $d(1000 * data.etime, 'long', $store.getters.langs[$i18n.locale].locale) }}
                </template>
                <template v-else>
                  -
                </template>
              </td>
            </tr>
            <tr>
              <td>
                {{ $i18n.t("message.translation_preset.preset") }}
                <template v-if="mode === 'user' && !registable_parameters">
                  <br />
                  <span class="red--text">
                    ({{ $t("message.translation_preset.select_translate_engine") }})
                  </span>
                </template>
              </td>
              <td>
                <template v-if="parameters">
                  <template v-if="mode === 'user'">
                    <TranslationPresetParameter
                      ref="translate_preset_parameter"
                      :visible_panel="visible_panel()"
                      :mode="mode"
                      :initial_parameters="parameters"
                      v-on:update_changable="changable_parameters = $event"
                      v-on:update_registable="registable_parameters = $event"
                      :panel="panel"
                      v-on:update_panel="panel = $event"
                      :is_xml="true"
                      command_type="translate"
                    >
                    </TranslationPresetParameter>
                  </template>
                  <template v-else>
                    <JobParameters
                      :mode="mode"
                      type="translate"
                      :parameters="parameters"
                      :nav_path="nav_path"
                      :expand="true"
                    >
                    </JobParameters>
                  </template>
                </template>
                <template v-else>
                  -
                </template>
              </td>
            </tr>
            <tr>
              <td>
                {{ $i18n.t("message.common.operation") }}
              </td>
              <td>
                <div class="buttonContents">
                  <template v-if="deletable()">
                    <v-btn
                      @click="confirm_delete_data(get_prefix() + 'translation_preset', [data.tid])"
                    >
                      {{ $t("message.common.delete") }}
                    </v-btn>
                  </template>
                </div>
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
      <p class="text-right">
        <template v-if="changeable()">
          <v-btn
            color="primary"
            :disabled="!modifiable && !changable_parameters"
            @click="change_all('translation_preset', [data.tid])"
          >
            {{ $t("message.common.modify") }}
          </v-btn>
        </template>
        <v-btn :to="get_prev(get_prefix() + 'translation_preset')">
          {{ $t("message.common.prev") }}
        </v-btn>
      </p>
    </v-card>
    <v-card class="pa-5 mt-1" v-else>
      {{ $t("message.common.no_id", { "id": $route.params.tid }) }}
      <p class="text-right">
        <v-btn :to="get_prev(get_prefix() + 'translation_preset')">
          {{ $t("message.common.prev") }}
        </v-btn>
      </p>
    </v-card>
    <Confirm
      ref="delete_confirm"
      :message="$t('message.common.delete_confirm')"
      :positive="$t('message.common.yes')"
      :negative="$t('message.common.no')"
    >
    </Confirm>
  </div>
</template>

<script>
import Breadcrumbs from '../components/Breadcrumbs';
import IdName from '../components/IdName';
import TranslationPresetParameter from '../components/TranslationPresetParameter';
import JobParameters from '../components/JobParameters';
import Confirm from '../components/Confirm';
import { res_detail } from "../js/res_detail";
import { http_error } from "../js/http_error";
import sanitize_filename from "sanitize-filename";
import _cloneDeep from "lodash/cloneDeep";

export default {
  name: 'translation_preset_detail',
  components: {
    Breadcrumbs,
    IdName,
    TranslationPresetParameter,
    JobParameters,
    Confirm
  },
  mixins: [
    res_detail,
    http_error
  ],
  props: {
    mode: String
  },
  data: function(){
    return {
      module_id: "28",
      nav_path: null,
      fetch_status: "getting",
      data: null,
      original_data: null,
      editable: {
        title: () => (this.data && this.data.title.length > 0 && this.data.title.length <= 80)
      },
      permissions: [
        {
          "label": "user",
          "value": "user"
        },
        {
          "label": "group",
          "value": "group"
        },
        {
          "label": "organization",
          "value": "organization"
        }
      ],
      revision: null,
      parameters: null,
      changable_parameters: false,
      registable_parameters: true,
      panel: 0
    }
  },
  mounted: function(){
    this.refresh()
  },
  methods: {
    refresh: function(){
      this.get_detail_data(this.get_prefix() + 'translation_preset/', [this.$route.params.tid])
        .then( response => {
          if(response){
            return this.$http.get(this.$store.getters.api_endpoint + "/" + this.get_prefix() + "translation_preset/" + this.$route.params.tid + "/json");
          }
        })
        .then( response => {
          if(response && "data" in response){
            if(this.$store.getters.debug){
              console.log("response.data:" + JSON.stringify(response.data));
            }
            if("format" in response.data
              && "type" in response.data.format
              && response.data.format.type === "translation_preset"
              && "contents" in response.data){
              this.revision = response.data.revision;
              this.parameters = response.data.contents;
              if(this.$refs.translate_preset_parameter){
                this.$refs.translate_preset_parameter.clear();
                this.$refs.translate_preset_parameter.mounted(this.parameters);
              }
            }
          }
        })
        .catch( error => {
          if(this.http_error(error)){
            this.$store.commit("setNotification", {
              type: "error",
              message: error.response.data
            });
          }
        });
    },
    visible_panel: function(){
      var visible_panel = ['field_model', 'detail'];
      if(["t-3mt"].indexOf(this.$store.getters.domain) >= 0){
        visible_panel.push('corpuses', 'terms');
      }
      return visible_panel;
    },
    change_all: function(module, ids){
      if(this.modifiable){
        this.change_data(module, ids);
      }
      if(this.changable_parameters
        && this.$refs.translate_preset_parameter){
        var translate_preset_parameter = this.$refs.translate_preset_parameter;
        var now = Math.floor(new Date().getTime() / 1000);
        var parameters = _cloneDeep(translate_preset_parameter.parameters);
        delete parameters.title;
        if(translate_preset_parameter.field_tab === "field"){
          parameters.model_type = "field";
          parameters.model = translate_preset_parameter.selected_field[0];
        }else{
          parameters.model_type = "model";
          parameters.model = translate_preset_parameter.selected_model[0].mid;
        }
        if(translate_preset_parameter.selected_corpuses.length > 0){
          parameters.corpuses = [];
          for(var c of translate_preset_parameter.selected_corpuses){
            parameters.corpuses.push(c.cid);
          }
        }
        if(translate_preset_parameter.selected_terms.length > 0){
          parameters.terms = [];
          for(var t of translate_preset_parameter.selected_terms){
            parameters.terms.push(t.yid);
          }
        }
        if(!translate_preset_parameter.tag_available(true)){
          delete parameters.detail.tag_option.tag_type;
        }
        if(Object.keys(parameters.detail.tag_option).length === 0){
          delete parameters.detail.tag_option;
        }
        if(!translate_preset_parameter.style_option_ja_available){
          delete parameters.detail.style_option_ja.character_style;
          delete parameters.detail.style_option_ja.parenthesis_style;
          delete parameters.detail.style_option_ja.sentence_style;
        }
        if(Object.keys(parameters.detail.style_option_ja).length === 0){
          delete parameters.detail.style_option_ja;
        }
        if(!translate_preset_parameter.score_threshold_available){
          delete parameters.detail.score_threshold.custom_model;
        }
        if(Object.keys(parameters.detail.score_threshold).length === 0){
          delete parameters.detail.score_threshold;
        }
        if(!translate_preset_parameter.bullet_feature_available){
          delete parameters.detail.bullet_option.bullet_feature;
        }
        if(Object.keys(parameters.detail.bullet_option).length === 0){
          delete parameters.detail.bullet_option;
        }
        if(Object.keys(parameters.detail).length === 0){
          delete parameters.detail;
        }
        var json_parameters = {
          contents: parameters,
          create: {
            uid: this.$store.getters.user.uid,
            utime: now
          },
          format: {
            type: "translation_preset",
            version: 1
          },
          revision: this.revision + 1,
        };

        var url = this.$store.getters.api_endpoint + "/translation_preset/" + this.$route.params.tid + "/json";
        var blob = new Blob([JSON.stringify(json_parameters)], { type: 'application/json' });
        var formData = new FormData();
        var filename = sanitize_filename(this.$route.params.tid + ".json").replace(";", "")
        formData.append('file', blob, filename);
        var take_over = {
          parent: this,
          parameters: parameters
        };
        this.$http.put(url,
                       formData,
                       {
                         headers: {
                           'Content-Type': 'multipart/form-data'
                         },
                       })
          .then(function(response){
            this.parent.$store.commit("setNotification", {
              type: "success",
              message: {
                eid: "E1" + this.parent.module_id + "005" + ":00000000",
                param: {}
              }
            });
            this.parent.parameters = this.parameters;
            this.parent.$refs.translate_preset_parameter.mounted(this.parameters);
            this.parent.revison++;
          }.bind(take_over))
          .catch( error => {
            if(this.http_error(error)){
              this.$store.commit("setNotification", {
                type: "error",
                message: error.response.data
              });
            }
          });
      }
    }
  }
}
</script>

<style scoped>
.v-data-table
  tbody
  tr:hover:not(.v-data-table__expanded__content) {
  background: inherit !important;
}
.v-data-table td:first-of-type {
  white-space : nowrap;
}
.buttonContents {
  margin-bottom: 8px;
}
.buttonContents > button {
  margin-top: 8px;
  margin-right: 8px;
}
.v-data-table {
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  margin-bottom: 12px;
}
.v-data-table
  tbody > tr > td:first-of-type {
  background-color: #eeeeee;
}
.table-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 0;
}
</style>
